import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Code,
  Compass,
  Globe,
  MapPin,
  Palette,
  PenTool,
  Server,
  type LucideIcon,
} from "lucide-react";

import OperationVisual from "@/components/OperationVisuals";
import SddMark from "@/components/SddMark";
import type { Operation, OperationService } from "@/components/Operations";

const SLOTS = [
  { area: "1 / 1 / 2 / 2", justify: "md:justify-end" },
  { area: "1 / 3 / 2 / 4", justify: "md:justify-start" },
  { area: "3 / 1 / 4 / 2", justify: "md:justify-end" },
  { area: "3 / 3 / 4 / 4", justify: "md:justify-start" },
  { area: "2 / 1 / 3 / 2", justify: "md:justify-end" },
  { area: "2 / 3 / 3 / 4", justify: "md:justify-start" },
];

const ELBOW = 14;

const SERVICE_ICONS: Record<OperationService, LucideIcon> = {
  productLead: Compass,
  fullstack: Server,
  frontend: Code,
  design: PenTool,
  brand: Palette,
  website: Globe,
  googleBusiness: MapPin,
};

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <p className="flex gap-2">
      <span className="text-ink-subtle shrink-0">{label}</span>
      <span className="text-ink-muted">{children}</span>
    </p>
  );
}

export default function OperationsCanvas({
  operations,
}: {
  operations: Operation[];
}) {
  const { t } = useTranslation();
  const placed = useMemo(
    () => operations.slice(0, SLOTS.length),
    [operations],
  );

  const canvasRef = useRef<HTMLDivElement>(null);
  const markRef = useRef<HTMLDivElement>(null);
  const nodeRefs = useRef<(HTMLDivElement | null)[]>([]);

  const [size, setSize] = useState({ width: 0, height: 0 });
  const [paths, setPaths] = useState<string[]>([]);

  const measure = useCallback(() => {
    const canvas = canvasRef.current;
    const mark = markRef.current;
    if (!canvas || !mark) return;

    const base = canvas.getBoundingClientRect();
    setSize((current) =>
      current.width === base.width && current.height === base.height
        ? current
        : { width: base.width, height: base.height },
    );

    const markBox = mark.getBoundingClientRect();
    const cx = markBox.left - base.left + markBox.width / 2;
    const cy = markBox.top - base.top + markBox.height / 2;

    const next = placed.map((_, index) => {
      const node = nodeRefs.current[index];
      if (!node) return "";

      const box = node.getBoundingClientRect();
      const left = box.left - base.left;
      const right = box.right - base.left;
      const bottom = box.bottom - base.top;
      const top = box.top - base.top;

      const above = box.top - base.top + box.height / 2 < cy;
      const targetX = left + box.width / 2 < cx ? right - 24 : left + 24;
      const targetY = above ? bottom : top;

      const dx = Math.sign(targetX - cx) || 1;
      const dy = Math.sign(targetY - cy) || 1;
      const radius = Math.min(
        ELBOW,
        Math.abs(targetX - cx),
        Math.abs(targetY - cy),
      );

      return [
        `M ${cx} ${cy}`,
        `H ${targetX - dx * radius}`,
        `Q ${targetX} ${cy} ${targetX} ${cy + dy * radius}`,
        `V ${targetY}`,
      ].join(" ");
    });

    setPaths((current) =>
      current.length === next.length && current.every((d, i) => d === next[i])
        ? current
        : next,
    );
  }, [placed]);

  useEffect(() => {
    measure();

    const canvas = canvasRef.current;
    if (!canvas) return;

    const observer = new ResizeObserver(measure);
    observer.observe(canvas);
    nodeRefs.current.forEach((node) => node && observer.observe(node));

    return () => observer.disconnect();
  }, [measure]);

  return (
    <div
      ref={canvasRef}
      className="canvas-grid relative rounded-xl border border-line overflow-hidden px-4 py-8 md:p-10"
    >
      <svg
        className="hidden md:block absolute inset-0 pointer-events-none"
        width={size.width}
        height={size.height}
        aria-hidden="true"
      >
        {paths.map((d, index) =>
          d ? (
            <g key={index}>
              <path className="canvas-link" d={d} />
              <path
                className="canvas-pulse"
                d={d}
                pathLength={100}
                style={{ animationDelay: `${index * -1.2}s` }}
              />
            </g>
          ) : null,
        )}
      </svg>

      <div className="relative flex flex-col items-center gap-6 md:grid md:grid-cols-3 md:grid-rows-[auto_auto_auto] md:gap-x-8 md:gap-y-12 md:items-center">
        <div
          ref={markRef}
          className="flex justify-center order-first md:order-none w-fit mx-auto"
          style={{ gridArea: "2 / 2 / 3 / 3" }}
        >
          <SddMark
            title="Suporte de Domingo"
            className="w-14 h-14 md:w-20 md:h-20 text-ink"
          />
        </div>

        {placed.map((operation, index) => (
          <div
            key={operation.client}
            className={`flex w-full justify-center ${SLOTS[index].justify}`}
            style={{ gridArea: SLOTS[index].area }}
          >
            <div
              ref={(node) => {
                nodeRefs.current[index] = node;
              }}
              className="w-full max-w-[17rem] rounded-md border border-line bg-card backdrop-blur-sm overflow-hidden font-mono text-[11px] leading-relaxed"
            >
              <OperationVisual kind={operation.kind} services={operation.services} />

              <div className="flex items-center gap-2 px-3 pt-2.5">
                <span className="op-pulse block h-1.5 w-1.5 rounded-full bg-brand shrink-0" />
                <span className="tracking-[0.14em] uppercase text-ink truncate">
                  {operation.client}
                </span>
                {operation.since && (
                  <span className="ml-auto text-ink-subtle tabular-nums shrink-0">
                    {operation.since}
                  </span>
                )}
              </div>

              <div className="flex flex-col gap-2.5 px-3 pt-1 pb-3">
                <p className="text-ink-subtle">
                  {operation.product ?? t(`operations.kinds.${operation.kind}`)}
                  {operation.seats && operation.seats > 1
                    ? ` · ${operation.seats} ${t("operations.seatsLabel")}`
                    : ""}
                </p>

                <ul className="flex flex-wrap gap-1.5">
                  {operation.services.map((service) => {
                    const Icon = SERVICE_ICONS[service];
                    return (
                      <li
                        key={service}
                        className="inline-flex items-center gap-1 rounded-full border border-line bg-surface-raised/60 px-2 py-0.5 text-ink-muted"
                      >
                        <Icon className="h-3 w-3 text-brand" aria-hidden="true" />
                        {t(`operations.services.${service}`)}
                      </li>
                    );
                  })}
                </ul>

                {operation.stack && operation.stack.length > 0 && (
                  <Row label="env">{operation.stack.join(" · ")}</Row>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
